import type { HTMLAttributes, ReactNode } from 'react';
import { clsx } from 'clsx';

export type BadgeTone =
  | 'neutral'
  | 'accent'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'signal';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone;
  size?: 'sm' | 'md';
  /** Solid instead of soft — reserve it for counts that must be seen. */
  solid?: boolean;
  icon?: ReactNode;
  /** Renders the label in the stamped face. */
  mono?: boolean;
}

const SOFT: Record<BadgeTone, string> = {
  neutral: 'bg-surface-2 text-ink-2 border-line',
  accent: 'bg-accent-soft text-accent-strong border-transparent',
  success:
    'bg-[color-mix(in_oklab,var(--c-success)_14%,transparent)] text-success border-transparent',
  warning:
    'bg-[color-mix(in_oklab,var(--c-warning)_16%,transparent)] text-warning border-transparent',
  danger: 'bg-[color-mix(in_oklab,var(--c-danger)_14%,transparent)] text-danger border-transparent',
  info: 'bg-[color-mix(in_oklab,var(--c-info)_14%,transparent)] text-info border-transparent',
  signal: 'bg-signal-soft text-signal border-transparent',
};

const SOLID: Record<BadgeTone, string> = {
  neutral: 'bg-ink-2 text-paper border-transparent',
  accent: 'bg-accent text-accent-ink border-transparent',
  success: 'bg-success text-white border-transparent',
  warning: 'bg-warning text-white border-transparent',
  danger: 'bg-danger text-white border-transparent',
  info: 'bg-info text-white border-transparent',
  signal: 'bg-signal text-white border-transparent',
};

/** A small labelled marker. Colour never carries meaning on its own — there is always text. */
export function Badge({
  tone = 'neutral',
  size = 'md',
  solid = false,
  icon,
  mono = false,
  className,
  children,
  ...rest
}: BadgeProps) {
  return (
    <span
      className={clsx(
        'inline-flex max-w-full items-center gap-1 rounded-sm border font-medium whitespace-nowrap',
        size === 'sm' ? 'h-5 px-1.5 text-label' : 'h-6 px-2 text-micro',
        mono && 'font-data tracking-[0.02em]',
        solid ? SOLID[tone] : SOFT[tone],
        className,
      )}
      {...rest}
    >
      {icon ? <span className="shrink-0 [&>svg]:h-3 [&>svg]:w-3">{icon}</span> : null}
      <span className="truncate">{children}</span>
    </span>
  );
}
